import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ComponentManager from '@/Management/ComponentManager';
import { IInlineEditingContextDTO, IIteratorDTO, IListViewDTO, IResultDTO } from "coreframetypings/dto";
import FindAndConnectLinkViewModel from '@/Components/Links/FindAndConnectLink/FindAndConnectLinkViewModel';
import Utilities from '@/Utility/Utilities';

export default class ExtIEGFindAndConnectLinkViewModel extends FindAndConnectLinkViewModel {

    /**
     * The current inline editing context
     */
    private inlineEditingContext: KnockoutObservable<IInlineEditingContextDTO>;

    /**
     * The list view's original static filter
     */
    private originalListViewFilter: KnockoutComputed<string>;
    private busyCallback: (isBusy: boolean) => void;
    public uniqueSubAssociation: boolean;

    public constructor(protected params: any) {
        super(params);
        this.inlineEditingContext = this.observableOrDefault(params.inlineEditingContext, null);
        this.originalListViewFilter = this.definePureComputed(() => {
            return this.listView()?.staticFilterExpression || '';
        });
        this.uniqueSubAssociation = this.paramOrDefault(params.uniqueSubAssociation, false);
        this.busyCallback = this.handlerOrDefault(params.busyCallback);
    }

    /**
     * @override
     */
    protected createIsVisibleObservable(): KnockoutObservable<boolean> {
        if (!Utilities.isNullOrUndefined(this.params.isVisible)) {
            return this.observableOrDefault(this.params.isVisible);
        }

        this.setRequiredObservables();

        return this.createWritableComputed(() => !this.params.context?.isSummaryViewMode() && this.listView()?.showButtonSelect);
    }

    /**
     * @override
     */
    protected openSearchListView(listView: IListViewDTO): void {
        if (Utilities.isNotBlank(this.inlineEditingContext().primarySubAssociation)) {
            // The filter must be created. So all PDO_IDs of the association are loaded
            this.busyCallback(true);

            RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
                pdo_id: this.inlineEditingContext().contextObjectPdoId,
                pdTypeName: this.inlineEditingContext().contextObjectPdTypeName,
                assoziationName: this.inlineEditingContext().contextRelationName,
                properties: [this.inlineEditingContext()?.primarySubAssociation + '.PDO_ID'],
                filter: '',
                itOperator: null,
                orderby: '',
                retrieveExtentCount: true
            }).then((iterator: IIteratorDTO) => {
                if (!this.validateStatus(iterator))
                    return [];

                if (iterator.extentCount == "0")
                    return [];

                return RADEnvironmentProxy.getInstance().getExtent({
                    iteratorID: iterator.id,
                    start: 0,
                    end: parseInt(iterator.extentCount)
                });
            }).then((pdoIdExtent: [string][]) => {
                const pdoIds = pdoIdExtent.map(pdoId => pdoId[0]);
                listView.staticFilterExpression = this.uniqueSubAssociation ? this.extendFilterForDuplicates(pdoIds) : this.originalListViewFilter();

                super.openSearchListView(listView);
            }).always(() => {
                this.busyCallback(false);
            })
        }
        else {
            super.openSearchListView(listView);
        }
    }

    /**
     * Extends the static filter by the amount of already connected objects
     * @param pdoIds
     */
    private extendFilterForDuplicates(pdoIds: string[]): string {
        if (pdoIds.length == 0)
            return this.originalListViewFilter();

        const duplicateFilter = pdoIds.length > 0 ? `this.PDO_ID NOT IN (${pdoIds.join(',')})` : '';

        if (Utilities.isNotBlank(this.originalListViewFilter()))
            return `(${this.originalListViewFilter()}) AND (${duplicateFilter})`;
        else
            return duplicateFilter;
    }

    /**
     * @override
     */
    protected callConnectOperation(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): JQuery.Promise<IResultDTO> {
        return RADEnvironmentProxy.getInstance().connectFromInlineEditing({
            inlineEditingContextId: this.inlineEditingContext()?.id,
            targetPdTypeName: topdTypeName,
            targetPdoIds: toPdoIds.map(pdoID => parseInt(pdoID))
        }).promise();
    }
}

ComponentManager.setViewModel('ExtIEGFindAndConnectLink', ExtIEGFindAndConnectLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');